import React, { useState, useEffect, useCallback } from 'react';
import HeroSection from '../components/HeroSection';
import axios from "axios";
import config from '../config/config';
import { X, Plus, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const HeroAdmin = ({ config: initialConfig = {}, slug = '', onSave, onClose }) => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [preset, setPreset] = useState('split-layout');
  const [title, setTitle] = useState('Discover Your Perfect Style');
  const [subtitle, setSubtitle] = useState('Explore our latest collection...');
  const [badge, setBadge] = useState('New Collection 2025');
  const [ctaPrimary, setCtaPrimary] = useState({ text: 'Shop Now', link: '/shop' });
  const [ctaSecondary, setCtaSecondary] = useState({ text: 'View Details', link: '#' });
  const [stats, setStats] = useState([
    { value: '500+', label: 'Products' },
    { value: '50k+', label: 'Happy Customers' },
    { value: '4.9', label: 'Avg Rating' },
  ]);
  const [product, setProduct] = useState(null);
  const [devMode, setDevMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fullScreenPreview, setFullScreenPreview] = useState(false);
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [allProducts, setAllProducts] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState(null);

  // Preview-only cart state
  const [localQuantities, setLocalQuantities] = useState({});

  const [container, setContainer] = useState({
    padding: 'py-16 md:py-24',
    background: 'bg-gradient-to-br from-gray-50 to-white',
    heroImage: '',
    backgroundImage: '',
    titleColor: '#111827',
    subtitleColor: '#4B5563',
    badgeBg: '#f3f4f6',
    badgeText: '#111827',
    ctaPrimaryBg: '#111827',
    ctaPrimaryText: '#ffffff',
    ctaSecondaryBorder: '#6b7280',
    ctaSecondaryText: '#374151',
  });

  // Load saved settings
  useEffect(() => {
    const loadHeroSettings = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        await axios.get(config.API_URL + "/sanctum/csrf-cookie", { withCredentials: true });

        let response;
        const isDuplicate = slug.includes('-');
        if (isDuplicate) {
          response = await axios.get(
            `${config.API_URL}/api/pullCustomSettings/${slug}`,
            { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
          );
        } else {
          response = await axios.get(
            config.API_URL + '/api/pullHeroSettings',
            { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
          );
        }

        const dbData = isDuplicate ? (response.data.customSettings || {}) : (response.data.heroSettings || {});

        setPreset(dbData.preset || 'split-layout');
        setTitle(dbData.title || title);
        setSubtitle(dbData.subtitle || subtitle);
        setBadge(dbData.badge || badge);
        setCtaPrimary(dbData.ctaPrimary || ctaPrimary);
        setCtaSecondary(dbData.ctaSecondary || ctaSecondary);
        setStats(dbData.stats || stats);
        setProduct(dbData.product || null);
        setDevMode(dbData.devMode || false);
        setContainer(dbData.customStyles?.container || container);
      } catch (err) {
        console.error("Failed to load hero settings", err);
      } finally {
        setLoading(false);
      }
    };

    loadHeroSettings();
  }, [token, slug]);

  // Fetch products for modal
  useEffect(() => {
    if (!token || !['product-showcase', 'product-grid'].includes(preset)) return;

    const fetchProducts = async () => {
      try {
        const res = await axios.get(`${config.API_URL}/api/pullProducts/all`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setAllProducts(res.data?.data || res.data || []);
      } catch (err) {
        console.error("Failed to load products", err);
      }
    };

    fetchProducts();
  }, [token, preset]);

  // Cart logic matching ProductSection (optimistic update + API + rollback)
  const handleAddToCartPreview = useCallback(async (prod) => {
    if (!prod?.id) return;

    const productId = prod.id;

    // Optimistic +1
    setLocalQuantities((prev) => ({
      ...prev,
      [productId]: (prev[productId] || 0) + 1,
    }));

    try {
      await axios.get(config.API_URL + "/sanctum/csrf-cookie", { withCredentials: true });
      const xsrfToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('XSRF-TOKEN='))
        ?.split('=')[1];

      const response = await axios.post(
        `${config.API_URL}/api/addToCart2/${productId}`,
        {
          product_id: productId,
          quantity: 1,
          session: localStorage.getItem('session_id') || `guest-${Date.now()}`,
        },
        {
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "X-XSRF-TOKEN": decodeURIComponent(xsrfToken || ''),
            Authorization: token ? `Bearer ${token}` : undefined,
          },
          withCredentials: true,
        }
      );

      if (response.data?.cartItem?.quantity) {
        setLocalQuantities((prev) => ({
          ...prev,
          [productId]: response.data.cartItem.quantity,
        }));
      }
    } catch (err) {
      console.error('Add to cart failed (preview):', err);
      // Rollback
      setLocalQuantities((prev) => {
        const newState = { ...prev };
        if (newState[productId] <= 1) {
          delete newState[productId];
        } else {
          newState[productId] -= 1;
        }
        return newState;
      });
    }
  }, [token]);

  const saveHeroSettings = async () => {
    try {
      setSaving(true);
      await axios.get(config.API_URL + "/sanctum/csrf-cookie", { withCredentials: true });
      const xsrfToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('XSRF-TOKEN='))
        ?.split('=')[1];

      const endpoint = slug ? `/api/saveHeroSettings/${slug}` : '/api/saveHeroSettings';

      const payload = {
        preset,
        title,
        subtitle,
        badge,
        ctaPrimary,
        ctaSecondary,
        stats,
        product,
        devMode,
        customStyles: { container },
      };

      const response = await axios.post(
        config.API_URL + endpoint,
        { settings: payload },
        {
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "X-XSRF-TOKEN": decodeURIComponent(xsrfToken || ''),
            Authorization: `Bearer ${token}`,
          },
          withCredentials: true,
        }
      );

      if (response.data.status === 1) {
        alert("Hero saved successfully!");
        if (onSave) onSave(payload);
      } else {
        alert("Save failed.");
      }
    } catch (err) {
      console.error(err);
      alert("Failed to save hero section.");
    } finally {
      setSaving(false);
    }
  };

  const addStat = () => setStats([...stats, { value: 'New', label: 'Label' }]);
  const updateStat = (index, field, value) => {
    const newStats = [...stats];
    newStats[index][field] = value;
    setStats(newStats);
  };
  const removeStat = (index) => setStats(stats.filter((_, i) => i !== index));

  const openProductModal = () => {
    setSelectedProductId(product?.id || null);
    setIsProductModalOpen(true);
  };

  const confirmSelectProduct = () => {
    if (!selectedProductId) {
      setProduct(null);
      setIsProductModalOpen(false);
      return;
    }

    const selected = allProducts.find(p => p.id === selectedProductId);
    if (selected) {
      setProduct({
        id: selected.id,
        name: selected.name,
        price: parseFloat(selected.price) || 0,
        originalPrice: parseFloat(selected.regular_price) || null,
        image: selected.image_url || '',
        rating: selected.rating || 4.8,
        reviews: selected.reviews_count || 0,
      });
    }
    setIsProductModalOpen(false);
  };

  const isProductPreset = ['product-showcase', 'product-grid'].includes(preset);
  const showStats = ['split-layout', 'centered-dark', 'image-overlay'].includes(preset);

  const currentConfig = {
    preset,
    title,
    subtitle,
    badge,
    ctaPrimary,
    ctaSecondary,
    stats: showStats ? stats : [],
    product: isProductPreset ? product : undefined,
    devMode,
    customStyles: { container },
    handleAddToCart: handleAddToCartPreview,
    localQuantities,
    navigate, // pass navigate so HeroSection can use it
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Preview */}
      <div className="flex-1 relative">
        <div className="absolute inset-0 flex flex-col">
          <div className="bg-white border-b px-6 py-4 flex justify-between items-center shadow-sm">
            <h1 className="text-2xl font-bold text-gray-900">Hero Editor</h1>
            <button
              onClick={() => setFullScreenPreview(true)}
              className="bg-indigo-600 text-white px-5 py-2.5 rounded-lg hover:bg-indigo-700"
            >
              Full Preview
            </button>
          </div>
          <div className="flex-1 overflow-auto bg-gray-50 p-6">
            <HeroSection config={currentConfig} />
          </div>
        </div>
      </div>

      {/* Settings Sidebar */}
      <div className="w-96 bg-white border-l shadow-2xl overflow-y-auto">
        <div className="p-6 space-y-6">
          <h2 className="text-xl font-bold text-gray-900">Hero Settings</h2>

          {/* Preset */}
          <div>
            <h3 className="font-medium mb-2">Layout Preset</h3>
            <div className="grid gap-2">
              {['split-layout', 'centered-dark', 'image-overlay', 'product-showcase', 'product-grid'].map(p => (
                <button
                  key={p}
                  onClick={() => setPreset(p)}
                  className={`p-3 border rounded-lg text-left transition ${
                    preset === p
                      ? 'border-indigo-500 bg-indigo-50 font-medium'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {p.replace('-', ' ').replace(/\b\w/g, c => c.toUpperCase())}
                </button>
              ))}
            </div>
          </div>

          {/* Title / Subtitle / Badge */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Main Title</label>
              <input
                value={title}
                onChange={e => setTitle(e.target.value)}
                className="w-full border rounded-md px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Subtitle</label>
              <textarea
                value={subtitle}
                onChange={e => setSubtitle(e.target.value)}
                className="w-full border rounded-md px-3 py-2 h-20"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Badge Text (optional)</label>
              <input
                value={badge}
                onChange={e => setBadge(e.target.value)}
                className="w-full border rounded-md px-3 py-2"
                placeholder="e.g. New Season"
              />
            </div>
          </div>

          {/* CTAs */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Primary Button</label>
              <input
                value={ctaPrimary.text}
                onChange={e => setCtaPrimary({ ...ctaPrimary, text: e.target.value })}
                className="w-full border rounded px-3 py-2 mb-2"
                placeholder="Button label"
              />
              <input
                value={ctaPrimary.link}
                onChange={e => setCtaPrimary({ ...ctaPrimary, link: e.target.value })}
                className="w-full border rounded px-3 py-2"
                placeholder="/shop or https://..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Secondary Button (optional)</label>
              <input
                value={ctaSecondary.text}
                onChange={e => setCtaSecondary({ ...ctaSecondary, text: e.target.value })}
                className="w-full border rounded px-3 py-2 mb-2"
                placeholder="Button label"
              />
              <input
                value={ctaSecondary.link}
                onChange={e => setCtaSecondary({ ...ctaSecondary, link: e.target.value })}
                className="w-full border rounded px-3 py-2"
                placeholder="# or external link"
              />
            </div>
          </div>

          {/* Product Selection */}
          {isProductPreset && (
            <div className="border-t pt-5">
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-medium">Featured Product</h3>
                <button
                  onClick={openProductModal}
                  className="text-sm bg-indigo-600 text-white px-3 py-1.5 rounded hover:bg-indigo-700 flex items-center gap-1"
                >
                  <Plus size={16} /> {product ? 'Change' : 'Select'}
                </button>
              </div>
              {product && (
                <div className="bg-gray-50 p-3 rounded border flex items-center gap-3">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-16 h-16 object-cover rounded"
                    onError={e => e.target.src = 'https://placehold.co/64x64?text=No+Img'}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{product.name}</p>
                    <p className="text-xs text-gray-600">
                      KES {product.price.toLocaleString()}
                    </p>
                  </div>
                  <button
                    onClick={() => setProduct(null)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Stats */}
          {showStats && (
            <div className="border-t pt-5">
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-medium">Stats ({stats.length})</h3>
                <button
                  onClick={addStat}
                  className="text-sm text-indigo-600 hover:text-indigo-800"
                >
                  + Add
                </button>
              </div>
              <div className="space-y-3">
                {stats.map((stat, i) => (
                  <div key={i} className="bg-gray-50 p-3 rounded border flex gap-3 items-center">
                    <input
                      value={stat.value}
                      onChange={e => updateStat(i, 'value', e.target.value)}
                      className="w-20 border rounded px-2 py-1 text-center"
                      placeholder="500+"
                    />
                    <input
                      value={stat.label}
                      onChange={e => updateStat(i, 'label', e.target.value)}
                      className="flex-1 border rounded px-2 py-1"
                      placeholder="Happy Customers"
                    />
                    <button
                      onClick={() => removeStat(i)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* IMAGE URL INPUTS (replaced upload) */}
          <div className="border-t pt-5 space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Hero / Product Image URL</label>
              <input
                type="url"
                value={container.heroImage}
                onChange={e => setContainer(prev => ({ ...prev, heroImage: e.target.value }))}
                placeholder="https://example.com/hero.jpg"
                className="w-full border rounded px-3 py-2 text-sm"
              />
              {container.heroImage && (
                <img
                  src={container.heroImage}
                  alt="Hero preview"
                  className="mt-2 w-full h-32 object-cover rounded border"
                  onError={e => (e.target.style.display = 'none')}
                />
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Background Image URL (optional)</label>
              <input
                type="url"
                value={container.backgroundImage}
                onChange={e => setContainer(prev => ({ ...prev, backgroundImage: e.target.value }))}
                placeholder="https://example.com/bg.jpg"
                className="w-full border rounded px-3 py-2 text-sm"
              />
              {container.backgroundImage && (
                <img
                  src={container.backgroundImage}
                  alt="Background preview"
                  className="mt-2 w-full h-32 object-cover rounded border"
                  onError={e => (e.target.style.display = 'none')}
                />
              )}
            </div>
          </div>

          {/* Dev Mode */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold text-amber-900">Custom Styles (Dev Mode)</h3>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={devMode}
                  onChange={() => setDevMode(!devMode)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:bg-amber-600 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5"></div>
              </label>
            </div>

            {devMode && (
              <div className="space-y-4 text-sm">
                <div>
                  <label className="block font-medium mb-1">Padding (Tailwind)</label>
                  <input
                    value={container.padding}
                    onChange={e => setContainer({ ...container, padding: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                    placeholder="py-16 md:py-24"
                  />
                </div>
                <div>
                  <label className="block font-medium mb-1">Background (color / gradient)</label>
                  <input
                    value={container.background}
                    onChange={e => setContainer({ ...container, background: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                    placeholder="bg-white or linear-gradient(...)"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block font-medium mb-1">Title Color</label>
                    <input
                      type="color"
                      value={container.titleColor}
                      onChange={e => setContainer({ ...container, titleColor: e.target.value })}
                      className="w-full h-10 rounded border"
                    />
                  </div>
                  <div>
                    <label className="block font-medium mb-1">Subtitle Color</label>
                    <input
                      type="color"
                      value={container.subtitleColor}
                      onChange={e => setContainer({ ...container, subtitleColor: e.target.value })}
                      className="w-full h-10 rounded border"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block font-medium mb-1">Primary Btn Bg</label>
                    <input
                      type="color"
                      value={container.ctaPrimaryBg}
                      onChange={e => setContainer({ ...container, ctaPrimaryBg: e.target.value })}
                      className="w-full h-10 rounded border"
                    />
                  </div>
                  <div>
                    <label className="block font-medium mb-1">Primary Btn Text</label>
                    <input
                      type="color"
                      value={container.ctaPrimaryText}
                      onChange={e => setContainer({ ...container, ctaPrimaryText: e.target.value })}
                      className="w-full h-10 rounded border"
                    />
                  </div>
                </div>

                {preset !== 'product-grid' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block font-medium mb-1">Badge Bg</label>
                      <input
                        type="color"
                        value={container.badgeBg}
                        onChange={e => setContainer({ ...container, badgeBg: e.target.value })}
                        className="w-full h-10 rounded border"
                      />
                    </div>
                    <div>
                      <label className="block font-medium mb-1">Badge Text</label>
                      <input
                        type="color"
                        value={container.badgeText}
                        onChange={e => setContainer({ ...container, badgeText: e.target.value })}
                        className="w-full h-10 rounded border"
                      />
                    </div>
                  </div>
                )}

                {ctaSecondary.text && preset !== 'product-grid' && (
                  <div>
                    <label className="block font-medium mb-1">Secondary Border / Text</label>
                    <input
                      type="color"
                      value={container.ctaSecondaryBorder}
                      onChange={e => setContainer({ ...container, ctaSecondaryBorder: e.target.value })}
                      className="w-full h-10 rounded border"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Sticky Save Bar */}
        <div className="sticky bottom-0 bg-white border-t p-6 shadow-lg">
          <button
            onClick={saveHeroSettings}
            disabled={saving}
            className={`w-full py-4 rounded-xl font-bold text-white shadow transition ${
              saving ? 'bg-gray-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'
            }`}
          >
            {saving ? 'Saving...' : 'Save & Publish Hero'}
          </button>
        </div>
      </div>

      {/* Product Selection Modal */}
      {isProductModalOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b flex justify-between items-center">
              <h2 className="text-xl font-bold">Select Product for Hero</h2>
              <button
                onClick={() => setIsProductModalOpen(false)}
                className="text-gray-500 hover:text-gray-800 text-2xl"
              >
                <X size={28} />
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {allProducts.map(p => (
                  <div
                    key={p.id}
                    onClick={() => setSelectedProductId(p.id)}
                    className={`p-3 border rounded-lg cursor-pointer transition-all flex gap-3 items-center ${
                      selectedProductId === p.id
                        ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-200'
                        : 'hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <img
                      src={p.image_url || 'https://placehold.co/80x80?text=?'}
                      alt={p.name}
                      className="w-16 h-16 object-cover rounded border bg-gray-50"
                      onError={e => e.target.src = 'https://placehold.co/80x80?text=No+Img'}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{p.name}</p>
                      <p className="text-xs text-gray-600">
                        KES {(parseFloat(p.price) || 0).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {allProducts.length === 0 && (
                <p className="text-center text-gray-500 py-12">No products found</p>
              )}
            </div>

            <div className="border-t px-6 py-4 flex justify-end gap-4">
              <button
                onClick={() => setIsProductModalOpen(false)}
                className="px-6 py-2 border rounded-lg hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={confirmSelectProduct}
                className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                {selectedProductId ? 'Select Product' : 'Clear Product'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Full Screen Preview */}
      {fullScreenPreview && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex flex-col"
          onClick={() => setFullScreenPreview(false)}
        >
          <div className="bg-white/10 backdrop-blur px-6 py-4 flex justify-end">
            <button
              className="text-white text-5xl hover:text-gray-300"
              onClick={() => setFullScreenPreview(false)}
            >
              ×
            </button>
          </div>
          <div className="flex-1 overflow-auto p-6 md:p-10" onClick={e => e.stopPropagation()}>
            <HeroSection config={currentConfig} />
          </div>
        </div>
      )}
    </div>
  );
};

export default HeroAdmin;